import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, Inbox, FolderTree, Package, Image as ImageIcon, Settings, LogOut, ExternalLink } from "lucide-react";
import { Logo } from "../../components/site/Logo";
import { Overview } from "../../components/admin/Overview";
import { Inquiries } from "../../components/admin/Inquiries";
import { CategoriesAdmin } from "../../components/admin/CategoriesAdmin";
import { ProductsAdmin } from "../../components/admin/ProductsAdmin";
import { MediaAdmin } from "../../components/admin/MediaAdmin";
import { SettingsAdmin } from "../../components/admin/SettingsAdmin";
import { api } from "../../lib/api";
import { Seo } from "../../components/site/Seo";

const TABS = [
  ["overview", "Overview", LayoutDashboard, Overview],
  ["inquiries", "Inquiries", Inbox, Inquiries],
  ["categories", "Categories", FolderTree, CategoriesAdmin],
  ["products", "Products", Package, ProductsAdmin],
  ["media", "Media library", ImageIcon, MediaAdmin],
  ["settings", "Site settings", Settings, SettingsAdmin],
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const { data: me, isError, isLoading } = useQuery({ queryKey: ["me"], queryFn: () => api.get("/auth/me").then((r) => r.data), retry: false });

  useEffect(() => {
    if (isError || !localStorage.getItem("ari_token")) nav("/admin/login", { replace: true });
  }, [isError, nav]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("ari_token");
    nav("/admin/login");
  };

  if (isLoading || !me) return <div className="min-h-screen bg-slate-100" data-testid="admin-loading" />;
  const Active = TABS.find((t) => t[0] === tab)[3];

  return (
    <div data-testid="admin-dashboard-container" className="min-h-screen bg-slate-100 lg:flex">
      <Seo title="Admin | Autocrat Rubber Industries" description="Admin area." noindex />
      <aside className="bg-[#07090e] text-white lg:fixed lg:inset-y-0 lg:left-0 lg:w-64">
        <div className="flex items-center justify-between p-5 lg:block lg:p-6">
          <Logo />
          <button onClick={logout} data-testid="admin-logout-button-mobile" className="lg:hidden"><LogOut className="h-5 w-5" /></button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:mt-6 lg:flex-col lg:overflow-visible lg:pb-0">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`flex shrink-0 items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${tab === k ? "bg-[#0a6cff] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </nav>
        <div className="hidden space-y-1 p-3 lg:absolute lg:bottom-0 lg:block lg:w-full">
          <Link to="/" target="_blank" className="flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:text-white"><ExternalLink className="h-4 w-4" />View website</Link>
          <button onClick={logout} data-testid="admin-logout-button" className="flex w-full items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:text-white"><LogOut className="h-4 w-4" />Log out</button>
          <p className="px-4 pt-2 text-xs text-slate-400">{me.email}</p>
        </div>
      </aside>
      <main className="flex-1 p-5 sm:p-8 lg:ml-64 lg:p-10">
        <Active goTo={setTab} />
      </main>
    </div>
  );
}
